import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings as SettingsIcon, Shield, Users, Bell, Plug, FileText, Clock, Play, Zap, AlertTriangle, Bot, BookOpen, Sparkles, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SCENARIOS = [
  { id: 'SCENARIO_MEMORY_LEAK', label: 'Memory Leak (P1)', desc: 'OOMKilled pods in payment-service', severity: 'critical', icon: Zap, agent: 'ARIA' },
  { id: 'SCENARIO_DB_CONN_POOL', label: 'DB Connection Pool (P1)', desc: 'Auth-service connection exhaustion', severity: 'critical', icon: Zap, agent: 'ARIA' },
  { id: 'SCENARIO_DISK_PRESSURE', label: 'Disk Pressure (P2)', desc: 'Elasticsearch disk at 92%', severity: 'high', icon: AlertTriangle, agent: 'ARIA' },
  { id: 'SCENARIO_CERT_EXPIRY', label: 'Certificate Expiry (P2)', desc: 'SSL cert expiring in 72h', severity: 'high', icon: Shield, agent: 'ARIA' },
  { id: 'SCENARIO_LATENCY_SPIKE', label: 'Latency Spike (P2)', desc: 'P99 >5s on checkout-service', severity: 'high', icon: AlertTriangle, agent: 'ARIA' },
  { id: 'SCENARIO_ALERT_STORM', label: 'Alert Storm (P3)', desc: '50+ alerts from k8s cluster', severity: 'medium', icon: AlertTriangle, agent: 'ARIA' },
  { id: 'SCENARIO_AMBIGUOUS_RCA', label: 'Ambiguous RCA (P2)', desc: 'Multiple possible causes', severity: 'high', icon: AlertTriangle, agent: 'ARIA' },
  // Phase 3 agent scenarios
  { id: 'CHG_LOW_RISK_STANDARD', label: 'Change: Low Risk', desc: 'SSL cert rotation on staging', severity: 'low', icon: Shield, agent: 'CHANGE-IQ' },
  { id: 'CHG_HIGH_RISK_BLOCK', label: 'Change: High Risk', desc: 'Production DB migration', severity: 'critical', icon: Zap, agent: 'CHANGE-IQ' },
  { id: 'PRB_PATTERN_DETECTION', label: 'Problem: Pattern Detect', desc: 'NOVA incident pattern analysis', severity: 'medium', icon: AlertTriangle, agent: 'NOVA' },
  { id: 'ATLAS_DRIFT_SCAN', label: 'Asset: Discovery Scan', desc: 'ATLAS CMDB drift detection', severity: 'medium', icon: Shield, agent: 'ATLAS' },
  // Phase 4 agent scenarios
  { id: 'CAPACITOR_ANALYSIS', label: 'AIOps: Capacity Analysis', desc: 'Metric analysis & predictions', severity: 'medium', icon: AlertTriangle, agent: 'CAPACITOR' },
  { id: 'HERALD_BROADCAST', label: 'Comms: Major Incident', desc: 'Stakeholder notification broadcast', severity: 'high', icon: AlertTriangle, agent: 'HERALD' },
  { id: 'SENTINL_THREAT', label: 'Security: Threat Detection', desc: 'Brute force or data exfiltration', severity: 'critical', icon: Shield, agent: 'SENTINL' },
  { id: 'SENTINL_MALWARE', label: 'Security: Malware Found', desc: 'Cryptominer on staging cluster', severity: 'critical', icon: Zap, agent: 'SENTINL' },
  // Phase 4 Intelligence Layer
  { id: 'COMPLIANCE_REPORT', label: 'Compliance: SOC 2 Report', desc: 'Generate compliance report', severity: 'low', icon: Shield, agent: 'COMPLIANCE-OPS' },
  { id: 'AUDIT_CHAIN', label: 'Compliance: Verify Chain', desc: 'Verify audit trail integrity', severity: 'low', icon: Shield, agent: 'COMPLIANCE-OPS' },
  { id: 'LEARNX_GENERATE', label: 'Knowledge: Auto-Generate', desc: 'Generate KB articles from resolved incidents', severity: 'low', icon: Shield, agent: 'LEARN-X' },
  // Phase 4 E2E Demo Scenarios
  { id: 'REAL_ALERT', label: 'E2E: Real Alert Pipeline', desc: 'Datadog → MOA → ARIA end-to-end', severity: 'high', icon: Zap, agent: 'CAPACITOR→ARIA' },
  { id: 'PREDICTION', label: 'E2E: Anomaly Prediction', desc: 'Correlated anomaly 87% confidence', severity: 'high', icon: AlertTriangle, agent: 'CAPACITOR' },
  { id: 'NOISE_SUPPRESS', label: 'E2E: Noise Suppression', desc: '20 dupes → 1 incident (95% reduction)', severity: 'medium', icon: Shield, agent: 'CAPACITOR' },
  { id: 'HERALD_P1', label: 'E2E: P1 Notification Blast', desc: 'Multi-channel stakeholder alert', severity: 'critical', icon: Zap, agent: 'HERALD' },
  { id: 'HERALD_DIGEST', label: 'E2E: Daily Digest', desc: 'Executive summary generation', severity: 'low', icon: Shield, agent: 'HERALD' },
  { id: 'SENTINL_GUARDRAIL', label: 'E2E: Security Guardrail', desc: 'Block lockout without approval', severity: 'critical', icon: Shield, agent: 'SENTINL' },
  { id: 'SLA_WARNING', label: 'E2E: SLA Breach Warning', desc: 'P1 breach warning at 4h threshold', severity: 'high', icon: AlertTriangle, agent: 'SLA_ENGINE' },
];

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [triggeringId, setTriggeringId] = useState(null);

  const triggerMutation = useMutation({
    mutationFn: async (scenarioId) => {
      const token = localStorage.getItem('nexus_token');
      const { data } = await axios.post(`${BACKEND_URL}/api/dev/trigger-scenario`, { scenario_id: scenarioId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Scenario triggered: ${data.ticket_id}`, {
        description: `MOA routed to ${data.moa_decision?.assigned_agent} (${data.moa_decision?.confidence}% confidence)`,
      });
      queryClient.invalidateQueries(['tickets']);
      queryClient.invalidateQueries(['nav-badges']);
      setTriggeringId(null);
    },
    onError: (err) => {
      toast.error('Failed to trigger scenario', { description: err.message });
      setTriggeringId(null);
    },
  });

  const handleTrigger = (scenarioId) => {
    setTriggeringId(scenarioId);
    triggerMutation.mutate(scenarioId);
  };

  return (
    <div data-testid="settings-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Settings</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Organization config, integrations, and agent scenarios</p>
        </div>
        <Link to="/onboarding" data-testid="link-onboarding"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--nexus-accent)] text-white text-xs font-semibold hover:bg-opacity-90 transition-colors">
          <Rocket size={14} /> Customer Onboarding Wizard
        </Link>
      </div>

      {/* Phase 5 Feature Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Link to="/settings/agents" data-testid="link-agent-config"
          className="nexus-card relative overflow-hidden p-4 hover:bg-white/[0.02] transition-colors group">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-accent)] bg-opacity-10">
              <Bot size={18} className="text-[var(--nexus-accent)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] group-hover:text-[var(--nexus-accent)] transition-colors">Agent Configuration</h3>
              <p className="text-[11px] text-[var(--nexus-text-muted)] mt-0.5">Tune confidence thresholds, SLA configs, and tool permissions per agent</p>
            </div>
          </div>
        </Link>
        <Link to="/analytics/query" data-testid="link-nlp-query"
          className="nexus-card relative overflow-hidden p-4 hover:bg-white/[0.02] transition-colors group">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-info)] bg-opacity-10">
              <Sparkles size={18} className="text-[var(--nexus-info)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] group-hover:text-[var(--nexus-info)] transition-colors">NLP Analytics Query</h3>
              <p className="text-[11px] text-[var(--nexus-text-muted)] mt-0.5">Ask questions about operations in natural language powered by Claude</p>
            </div>
          </div>
        </Link>
        <Link to="/docs/full" data-testid="link-docs"
          className="nexus-card relative overflow-hidden p-4 hover:bg-white/[0.02] transition-colors group">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-success)] bg-opacity-10">
              <BookOpen size={18} className="text-[var(--nexus-success)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] group-hover:text-[var(--nexus-success)] transition-colors">Full Documentation</h3>
              <p className="text-[11px] text-[var(--nexus-text-muted)] mt-0.5">Value proposition, architecture, pricing, user journeys, guides, patentable methods</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Phase 4 Feature Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Link to="/settings/compliance" data-testid="link-compliance"
          className="nexus-card relative overflow-hidden p-4 hover:bg-white/[0.02] transition-colors group">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-warning)] bg-opacity-10">
              <Shield size={18} className="text-[var(--nexus-warning)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] group-hover:text-[var(--nexus-warning)] transition-colors">Compliance & Reports</h3>
              <p className="text-[11px] text-[var(--nexus-text-muted)] mt-0.5">Generate SOC2/ISO/HIPAA reports, verify hash chain</p>
            </div>
          </div>
        </Link>
        <Link to="/settings/audit" data-testid="link-audit"
          className="nexus-card relative overflow-hidden p-4 hover:bg-white/[0.02] transition-colors group">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-accent)] bg-opacity-10">
              <Shield size={18} className="text-[var(--nexus-accent)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] group-hover:text-[var(--nexus-accent)] transition-colors">Audit Trail</h3>
              <p className="text-[11px] text-[var(--nexus-text-muted)] mt-0.5">Unified audit view, filtering, export, dashboard stats</p>
            </div>
          </div>
        </Link>
        <Link to="/settings/status" data-testid="link-status"
          className="nexus-card relative overflow-hidden p-4 hover:bg-white/[0.02] transition-colors group">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-primary-bright)] bg-opacity-10">
              <Zap size={18} className="text-[var(--nexus-primary-bright)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] group-hover:text-[var(--nexus-primary-bright)] transition-colors">System Status Page</h3>
              <p className="text-[11px] text-[var(--nexus-text-muted)] mt-0.5">Public status page auto-managed by HERALD, service health overview</p>
            </div>
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left column: Settings */}
        <div className="space-y-3">
          <Link to="/settings/team" className="block"><SettingCard icon={Users} title="Team Management" desc="Manage users, roles, and permissions" color="var(--nexus-primary-bright)" /></Link>
          <Link to="/settings/sla" className="block"><SettingCard icon={Shield} title="Security & SLA Policies" desc="SLA targets, notification routing, escalation thresholds" color="var(--nexus-danger)" /></Link>
          <Link to="/settings/sla" className="block"><SettingCard icon={Bell} title="Notification Routing" desc="Configure channel routing per event type (Slack, Email, Teams)" color="var(--nexus-warning)" /></Link>
          <Link to="/settings/integrations" className="block"><SettingCard icon={Plug} title="Integrations" desc="Connect monitoring tools, cloud providers, communication channels" color="var(--nexus-accent)" /></Link>
          <Link to="/settings/sla" className="block"><SettingCard icon={FileText} title="SLA Configuration" desc="Define response and resolution targets per priority" color="var(--nexus-success)" /></Link>

          {/* Scenario Trigger Panel */}
          <div className="nexus-card relative overflow-hidden">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <div className="flex items-center gap-2">
                <Play size={14} className="text-[var(--nexus-accent)]" />
                <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Agent Scenarios</h2>
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-warning)] bg-opacity-15 text-[var(--nexus-warning)]">DEV</span>
              </div>
              <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">Trigger mock incidents to demo MOA + ARIA agent flow</p>
            </div>
            <ScrollArea className="h-[350px]">
              <div className="p-2 space-y-1.5" data-testid="scenario-list">
                {SCENARIOS.map(s => {
                  const Icon = s.icon;
                  const isTriggering = triggeringId === s.id;
                  return (
                    <button key={s.id} onClick={() => handleTrigger(s.id)} disabled={isTriggering}
                      data-testid={`trigger-${s.id.toLowerCase()}`}
                      className="w-full flex items-start gap-2.5 p-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.03] disabled:opacity-50 text-left transition-colors">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        s.severity === 'critical' ? 'bg-red-500/10 text-red-400' : s.severity === 'high' ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'
                      }`}>
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-[var(--nexus-text-primary)]">{s.label}</span>
                        <p className="text-[10px] text-[var(--nexus-text-muted)] truncate">{s.desc}</p>
                        <span className="font-mono text-[9px] text-[var(--nexus-accent)]">{s.agent}</span>
                      </div>
                      {isTriggering ? (
                        <span className="text-[10px] font-mono text-[var(--nexus-accent)] animate-pulse">Running...</span>
                      ) : (
                        <Play size={12} className="text-[var(--nexus-text-muted)] flex-shrink-0 mt-0.5" />
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
}

const SettingCard = ({ icon: Icon, title, desc, color }) => (
  <button className="w-full nexus-card relative overflow-hidden p-4 text-left hover:bg-white/[0.02] transition-colors" data-testid={`settings-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start gap-3">
      <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)]">{title}</h3>
        <p className="text-[11px] text-[var(--nexus-text-muted)] mt-0.5">{desc}</p>
      </div>
    </div>
  </button>
);
